import { useCallback, useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { api, apiFetch } from "@/lib/api";
import { AudioRig } from "@/lib/audioRig";
import { readSSE } from "@/lib/sse";
import {
  EMPTY_USAGE, MAX_RECORDING_SECONDS, createChunker, micError, newKey, pickMime, stepFailure, stepSignal, supportsVoice,
} from "@/lib/voice";

// Tap-to-talk session: record → transcribe → streamed reply → spoken in parts. Every async step checks the session
// token, so interrupting or ending the session drops its results; requests already sent still finish so their real
// charges are counted in the usage breakdown.
export function useVoiceSession({ voice, presenceRef, levelRef }) {
  const qc = useQueryClient();
  const [status, setStatus] = useState("idle");
  const [turns, setTurns] = useState([]);
  const [usage, setUsage] = useState(EMPTY_USAGE);
  const [balance, setBalance] = useState(null);
  const [notice, setNotice] = useState(null);
  const [elapsed, setElapsed] = useState(0);
  const [conversationId, setConversationId] = useState(null);
  const [ended, setEnded] = useState(false);
  const ref = useRef(null);
  if (!ref.current) ref.current = { token: 0, rig: new AudioRig(), queue: [], pumping: false, chatOpen: false };
  const s = ref.current;
  s.voice = voice;

  const charge = (patch) => setUsage((u) => Object.fromEntries(Object.entries(u).map(([k, v]) => [k, v + (patch[k] || 0)])));
  const updateTurn = (id, patch) => setTurns((t) => t.map((x) => (x.id === id ? { ...x, ...patch } : x)));
  const refreshMe = () => qc.invalidateQueries({ queryKey: ["me"] });

  const releaseMic = () => {
    clearInterval(s.tick);
    s.stream?.getTracks().forEach((t) => t.stop());
    s.stream = null;
    s.rig.detachMic();
  };

  const fetchSpeech = async (item) => {
    for (let attempt = 0; ; attempt += 1) {
      const step = stepSignal(30_000);
      try {
        const res = await api.post("/voice/speak", { text: item.text, voice: s.voice }, { raw: true, signal: step.signal, headers: { "Idempotency-Key": item.key } });
        const blob = await res.blob();
        charge({ speakChars: item.text.length, speakCredits: Number(res.headers.get("X-Credits-Charged") || 0) });
        const bal = res.headers.get("X-Credits-Balance");
        if (bal) setBalance(Number(bal));
        return blob;
      } catch (e) {
        const lost = step.timedOut() || e?.status === 0;
        if (!lost || attempt >= 1) throw e;
      } finally {
        step.done();
      }
    }
  };

  const pump = async () => {
    const token = s.token;
    if (s.pumping) return;
    s.pumping = true;
    try {
      while (s.queue.length && token === s.token) {
        const [item, next] = s.queue;
        item.req ??= fetchSpeech(item);
        if (next) next.req ??= fetchSpeech(next);
        let blob;
        try {
          blob = await item.req;
        } catch (e) {
          if (token !== s.token) return;
          s.queue = [];
          s.mute = true;
          setNotice(e?.status === 402 ? stepFailure(e) : { kind: "warn", message: "Couldn't read the rest of that aloud — the full reply is shown below. Unread parts weren't charged." });
          break;
        }
        if (token !== s.token) return;
        s.queue.shift();
        setStatus("speaking");
        try {
          await s.rig.play(blob);
        } catch {
          s.queue = [];
          s.mute = true;
          setNotice({ kind: "warn", message: "Your browser blocked audio playback. The reply is shown as text — tap to talk to try again." });
          break;
        }
      }
    } finally {
      if (token === s.token) {
        s.pumping = false;
        if (!s.chatOpen) { setStatus("idle"); refreshMe(); }
      }
    }
  };

  const enqueue = (text) => {
    if (s.mute) return;
    s.queue.push({ text, key: newKey() });
    pump();
  };

  const reply = async (text, retry = false) => {
    const token = s.token;
    const id = newKey();
    setTurns((t) => [...t, { id, role: "assistant", text: "", status: "streaming" }]);
    setStatus("thinking");
    s.chatOpen = true;
    s.mute = false;
    s.replyId = id;
    const chunker = createChunker();
    const step = stepSignal(60_000);
    let got = "";
    let error = null;
    let started = false;
    try {
      const path = retry ? `/chat/conversations/${s.conv}/retry` : "/chat/messages";
      const body = retry ? undefined : { content: text, mode: "voice", ...(s.conv ? { conversation_id: s.conv } : {}) };
      const res = await apiFetch(path, { method: "POST", body, raw: true, signal: step.signal });
      for await (const { event, data } of readSSE(res)) {
        step.bump();
        if (event === "done") {
          charge({ replyCredits: data.credits_charged || 0 });
          if (data.balance != null) setBalance(data.balance);
          break;
        }
        // After an interrupt keep reading (the server stops quickly) only to count what the stopped answer cost.
        if (token !== s.token) continue;
        if (event === "meta") {
          started = true;
          s.conv = data.conversation_id;
          s.chatRequestId = data.request_id;
          setConversationId(data.conversation_id);
        } else if (event === "delta") {
          got += data.text;
          updateTurn(id, { text: got });
          chunker.push(data.text).forEach(enqueue);
        } else if (event === "error") {
          error = data.message;
        }
      }
    } catch (e) {
      if (token !== s.token) return;
      error = stepFailure(e, step.timedOut());
    } finally {
      step.done();
      if (token === s.token) { s.chatOpen = false; s.chatRequestId = null; }
    }
    if (token !== s.token) return;
    if (!got.trim()) {
      setTurns((t) => t.filter((x) => x.id !== id));
      const base = typeof error === "object" && error ? error : { kind: "error", message: error || "ChatSleek couldn't answer that. Please try again." };
      setNotice({ ...base, retry: base.kind === "credits" ? null : () => reply(text, started) });
      setStatus("idle");
      refreshMe();
      return;
    }
    chunker.flush().forEach(enqueue);
    updateTurn(id, { status: error ? "partial" : "done" });
    if (!s.queue.length && !s.pumping) { setStatus("idle"); refreshMe(); }
  };

  const transcribe = async (blob, key) => {
    const token = s.token;
    setStatus("transcribing");
    setNotice(null);
    const step = stepSignal(45_000);
    try {
      const r = await api.post("/voice/transcribe", blob, { signal: step.signal, headers: { "Idempotency-Key": key } });
      charge({ listenSeconds: r.seconds, listenCredits: r.credits_charged });
      if (r.balance != null) setBalance(r.balance);
      if (token !== s.token) return;
      if (!r.text.trim()) {
        setStatus("idle");
        setNotice({ kind: "info", message: "We didn't catch any words in that recording. Please try again." });
        return;
      }
      setTurns((t) => [...t, { id: key, role: "user", text: r.text }]);
      await reply(r.text);
    } catch (e) {
      if (token !== s.token) return;
      setStatus("idle");
      const lost = step.timedOut() || e?.status === 0;
      // A lost response keeps the same key, so the server returns the first result instead of charging again.
      setNotice(stepFailure(e, step.timedOut(), lost ? () => transcribe(blob, key) : e?.status >= 500 ? () => transcribe(blob, newKey()) : null));
      refreshMe();
    } finally {
      step.done();
    }
  };

  const listen = async () => {
    if (!supportsVoice()) return setNotice({ kind: "error", message: "This browser can't record audio. Try the latest Chrome, Safari, Edge or Firefox." });
    if (!navigator.onLine) return setNotice({ kind: "error", message: "You're offline. Connect to the internet to talk with JARVIS." });
    setNotice(null);
    s.rig.unlock();
    let stream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true } });
    } catch (e) {
      return setNotice(micError(e));
    }
    if (s.ended) {
      s.ended = false;
      setTurns([]);
      setUsage(EMPTY_USAGE);
      setConversationId(null);
      setEnded(false);
    }
    const mime = pickMime();
    const rec = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
    const chunks = [];
    rec.ondataavailable = (e) => e.data.size && chunks.push(e.data);
    rec.onstop = () => {
      releaseMic();
      const seconds = (Date.now() - s.startedAt) / 1000;
      if (s.discard) { s.discard = false; return; }
      if (seconds < 0.7) {
        setStatus("idle");
        setNotice({ kind: "info", message: "That was too short to hear. Tap, speak, then tap again to send." });
        return;
      }
      transcribe(new Blob(chunks, { type: (rec.mimeType || "audio/webm").split(";")[0] }), newKey());
    };
    Object.assign(s, { rec, stream, startedAt: Date.now(), discard: false });
    s.rig.attachMic(stream);
    rec.start(250);
    setElapsed(0);
    setStatus("listening");
    s.tick = setInterval(() => {
      const sec = (Date.now() - s.startedAt) / 1000;
      setElapsed(sec);
      if (sec >= MAX_RECORDING_SECONDS && rec.state === "recording") rec.stop();
    }, 200);
  };

  const send = () => {
    if (s.rec?.state === "recording") {
      setStatus("transcribing");
      s.rec.stop();
    }
  };

  const discardRecording = useCallback(() => {
    if (s.rec?.state === "recording") {
      s.discard = true;
      s.rec.stop();
    }
    releaseMic();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const interrupt = useCallback(() => {
    s.token += 1;
    if (s.chatRequestId) api.post(`/chat/requests/${s.chatRequestId}/stop`).catch(() => {});
    const speaking = s.pumping || s.queue.length > 0;
    Object.assign(s, { chatRequestId: null, chatOpen: false, queue: [], pumping: false });
    s.rig.stop();
    setTurns((t) => t.map((x) => (x.status === "streaming" ? { ...x, status: "interrupted" }
      : speaking && x.id === s.replyId && x.status === "done" ? { ...x, status: "stopped" } : x)));
    setStatus("idle");
    qc.invalidateQueries({ queryKey: ["me"] });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const end = () => {
    discardRecording();
    interrupt();
    s.conv = null;
    s.ended = true;
    setNotice(null);
    setEnded(true);
  };

  const tap = () => {
    if (status === "idle") listen();
    else if (status === "listening") send();
    else if (status === "thinking" || status === "speaking") interrupt();
  };

  const dismiss = () => setNotice(null);
  const retry = () => {
    const fn = notice?.retry;
    setNotice(null);
    fn?.();
  };

  useEffect(() => {
    const hidden = () => {
      if (document.hidden && s.rec?.state === "recording") {
        discardRecording();
        setStatus("idle");
        setNotice({ kind: "info", message: "Recording stopped because ChatSleek went into the background. Nothing was sent or charged." });
      }
    };
    document.addEventListener("visibilitychange", hidden);
    return () => document.removeEventListener("visibilitychange", hidden);
  }, [discardRecording]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const kind = status === "listening" ? "mic" : status === "speaking" ? "speech" : null;
    let raf;
    const loop = () => {
      const value = kind ? s.rig.level(kind) : 0;
      levelRef.current = { kind, value, playing: s.rig.playing() };
      presenceRef.current?.style.setProperty("--level", value.toFixed(3));
      if (kind) raf = requestAnimationFrame(loop);
    };
    loop();
    return () => cancelAnimationFrame(raf);
  }, [status, presenceRef, levelRef]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => () => {
    discardRecording();
    s.token += 1;
    if (s.chatRequestId) api.post(`/chat/requests/${s.chatRequestId}/stop`).catch(() => {});
    s.rig.close();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return { status, turns, usage, balance, notice, elapsed, conversationId, ended, tap, end, retry, dismiss };
}
